/**
 * Copyright (c) 2023 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { BlIcon } from '@blend/shared-react';
import { colors, mixins } from '@blend/consumer-components';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { NotificationItem } from '../Notifications/NotificationItem';
import { DocumentStatus } from '../../../shared/constants/document';
import { PopoverMenu } from '../../../components/NotaryRoom/PopoverMenu/PopoverMenu';
import { addNotifications } from '../../../actions/pageActions';
import { useRootSelector } from '../../../hooks/useRootSelector';
import { ResetDocumentConfirmationModal } from '../../../components/NotaryRoom/ResetDocumentConfirmationModal/ResetDocumentConfirmationModal';
import { isNotaryRoute } from '../../../utils/routing';
import { DocumentSigningType } from '../../../shared/constants/document';
import { DocumentStatusTag, getTagAttributes } from '../DocumentStatusTag/DocumentStatusTag';
import SigningDocument from './SigningDocument';
import { Document } from './Document';

export namespace DocumentPackage {
  export interface Props {
    handleDocumentClick: (documentId: string) => void;
    handleEditDocumentClick?: (documentId: string) => void;
    handleFinalizeDocumentClick?: (documentId: string) => void;
    documents: SigningDocument[];
    selectedDoc:
      | {
          id: string;
          pdfUrl: string | undefined;
        }
      | undefined;
    packageIndex: number;
  }
}

const styles = StyleSheet.create({
  headingWrapper: {
    width: '100%',
    backgroundColor: colors.catalystLight01,
    fontWeight: 'bold',
    ...mixins.borderTop(1, 'solid', mixins.globalBorderColor),
    ...mixins.borderBottom(1, 'solid', mixins.globalBorderColor),
  },
  headingTopWrapper: {
    display: 'flex',
    ...mixins.borderBottom(1, 'solid', mixins.globalBorderColor),
  },
  packageWrapper: {
    width: '100%',
    ...mixins.padding(8),
  },
  optionsWrapper: {
    display: 'flex',
    marginRight: '8px',
    alignContent: 'center',
    alignItems: 'center',
  },
  statusWrapper: {
    width: '100%',
    ...mixins.padding(4, 8, 6, 8),
  },
  dropdownIcon: {
    backgroundColor: 'transparent',
    border: 'none',
    alignItems: 'center',
    transition: 'transform 0.4s ease-out',
    ...mixins.margin(0, 10, 0, 0),
    ...mixins.padding(0),
  },
  dropdownCollapsedIcon: {
    transform: 'rotate(-90deg)',
  },
  popoverMenu: {
    ...mixins.padding(0, 0, 0, 6),
    ...mixins.borderLeft(1, 'solid', mixins.globalBorderColor),
  },
});

export const DocumentPackage: React.FC<DocumentPackage.Props> = ({
  handleDocumentClick = () => {},
  handleEditDocumentClick,
  handleFinalizeDocumentClick,
  documents,
  selectedDoc,
  packageIndex,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isNotary = isNotaryRoute(location.pathname);
  const { callDetails, signingRoom } = useRootSelector(state => ({
    signingRoom: state.signingRoom,
    callDetails: state.callDetails,
  }));
  const { signingAction } = signingRoom;

  const [isExpanded, setIsExpanded] = useState(true);
  const [isResetModalOpen, setResetModalOpen] = useState(false);

  if (documents.length === 0) {
    return null;
  }

  const isEditing = documents.some(doc => doc.status === DocumentStatus.EDITING);
  const isSigningActive = callDetails!.status === 'STARTED' || callDetails!.status === 'SIGNING';
  const packageContainsEnote = documents.some(d => d.type === DocumentSigningType.ENOTE);

  const statusTagAttributes = getTagAttributes(
    documents[0].status,
    !documents[0].allowDownload,
    documents[0].id === signingAction.document?.id,
    signingAction.type,
    signingAction.signerName,
  );

  const resetPackagePopoverItems = !isSigningActive
    ? [
        {
          name: 'ResetPackage',
          display: 'Reset document package',
          onClick: () => setResetModalOpen(!isResetModalOpen),
        },
      ]
    : [
        {
          name: 'ResetPackageError',
          display: 'Reset document package',
          onClick: () => {
            const notifications: NotificationItem.PropsWithoutId[] = [
              {
                type: 'error',
                iconType: 'error',
                content: 'In order to reset a document package, the document signing process must be stopped.',
                shouldAutoRemove: true,
              },
            ];
            addNotifications(notifications)(dispatch);
          },
        },
      ];

  const popoverItems = [...resetPackagePopoverItems];

  return (
    <div data-it={`documentPackage-${packageIndex}`}>
      <div className={css(styles.headingWrapper)}>
        <div className={css(styles.headingTopWrapper)}>
          <div className={css(styles.packageWrapper)}>Document package {packageIndex > 0 ? packageIndex + 1 : ''}</div>
          <div className={css(styles.optionsWrapper)}>
            <button
              data-it="ExpandButton"
              className={css(styles.dropdownIcon, !isExpanded && styles.dropdownCollapsedIcon)}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <BlIcon icon="caret-2" size="xs" />
            </button>
            {isNotary && !packageContainsEnote && (
              <span className={css(styles.popoverMenu)}>
                <PopoverMenu data-it="PackagePopover" iconType="more_horiz" menuItems={popoverItems}></PopoverMenu>
                <ResetDocumentConfirmationModal
                  isModalOpen={isResetModalOpen}
                  handleModalClose={() => setResetModalOpen(!isResetModalOpen)}
                  document={documents[0]}
                />
              </span>
            )}
          </div>
        </div>
        {statusTagAttributes && (
          <div className={css(styles.statusWrapper)}>
            <DocumentStatusTag
              indicatorType={statusTagAttributes.indicatorType}
              indicatorText={statusTagAttributes.indicatorText}
            />
          </div>
        )}
      </div>
      <span>
        {isExpanded &&
          documents.map(d => (
            <Document
              isSelected={selectedDoc?.id === d.id}
              key={d.id}
              document={d}
              handleClick={isEditing ? () => {} : handleDocumentClick}
              handleEditClick={handleEditDocumentClick}
              handleFinalizeClick={handleFinalizeDocumentClick}
            />
          ))}
      </span>
    </div>
  );
};
